import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import mammoth from "mammoth/mammoth.browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DownloadSimple } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/api";

function detect(name = "", ct = "") {
  const e = (name.split(".").pop() || "").toLowerCase();
  const c = (ct || "").toLowerCase();
  if (c.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(e)) return "img";
  if (c.includes("pdf") || e === "pdf") return "pdf";
  if (["xlsx", "xls", "csv"].includes(e) || c.includes("spreadsheet") || c.includes("excel") || c.includes("csv")) return "sheet";
  if (e === "docx" || c.includes("wordprocessingml")) return "word";
  if (c.startsWith("text/") || ["txt", "md", "log", "json", "xml"].includes(e)) return "text";
  return "other";
}

export default function DocViewer({ file, onClose }) {
  const open = !!file;
  const [st, setSt] = useState({ loading: true });

  useEffect(() => {
    if (!file) return;
    setSt({ loading: true });
    const url = fileUrl(file.storage_path);
    const kind = detect(file.name, file.content_type);
    if (kind === "img" || kind === "pdf") { setSt({ kind, url }); return; }
    if (kind === "sheet") {
      fetch(url).then((r) => r.arrayBuffer()).then((buf) => {
        const wb = XLSX.read(buf, { type: "array" });
        const sheets = wb.SheetNames.map((n) => ({ name: n, html: XLSX.utils.sheet_to_html(wb.Sheets[n]) }));
        setSt({ kind: "sheet", sheets, active: 0, url });
      }).catch(() => setSt({ kind: "other", url }));
      return;
    }
    if (kind === "word") {
      fetch(url).then((r) => r.arrayBuffer()).then((buf) => mammoth.convertToHtml({ arrayBuffer: buf }))
        .then((res) => setSt({ kind: "word", html: res.value, url })).catch(() => setSt({ kind: "other", url }));
      return;
    }
    if (kind === "text") {
      fetch(url).then((r) => r.text()).then((t) => setSt({ kind: "text", text: t, url })).catch(() => setSt({ kind: "other", url }));
      return;
    }
    setSt({ kind: "other", url });
  }, [file]);

  const download = () => {
    const a = document.createElement("a");
    a.href = st.url || fileUrl(file.storage_path);
    a.download = file.name || "document";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="doc-viewer">
        <DialogHeader><DialogTitle className="truncate pr-8">{file?.name}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] min-h-[200px] overflow-auto rounded-lg border border-border bg-white/[0.02]">
          {st.loading && <p className="p-6 text-sm text-muted-foreground">Se încarcă previzualizarea…</p>}
          {st.kind === "img" && <img src={st.url} alt={file?.name} className="mx-auto max-h-[68vh]" />}
          {st.kind === "pdf" && <iframe title={file?.name} src={st.url} className="h-[68vh] w-full" />}
          {st.kind === "text" && <pre className="whitespace-pre-wrap p-4 text-xs">{st.text}</pre>}
          {st.kind === "word" && <div className="max-w-none p-5 text-sm leading-relaxed text-foreground [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_p]:mb-2 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_img]:max-w-full [&_ul]:ml-5 [&_ul]:list-disc [&_ol]:ml-5 [&_ol]:list-decimal" dangerouslySetInnerHTML={{ __html: st.html || "" }} data-testid="doc-viewer-word" />}
          {st.kind === "sheet" && (
            <div data-testid="doc-viewer-sheet">
              {st.sheets.length > 1 && (
                <div className="flex flex-wrap gap-1 border-b border-border p-2">
                  {st.sheets.map((s, i) => (
                    <button key={s.name} onClick={() => setSt((x) => ({ ...x, active: i }))} className={`rounded px-2 py-0.5 text-xs ${st.active === i ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/5"}`} data-testid={`doc-sheet-tab-${i}`}>{s.name}</button>
                  ))}
                </div>
              )}
              <div className="overflow-auto p-2 text-xs [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:text-foreground" dangerouslySetInnerHTML={{ __html: st.sheets[st.active]?.html || "" }} />
            </div>
          )}
          {st.kind === "other" && <p className="p-6 text-sm text-muted-foreground">Previzualizarea nu e disponibilă pentru acest tip de fișier. Folosește „Descarcă" pentru a-l deschide local.</p>}
        </div>
        <div className="flex justify-end">
          <Button onClick={download} data-testid="doc-viewer-download"><DownloadSimple size={16} className="mr-1.5" /> Descarcă</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
